import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import './ChatWidget.css';

const CONTACT_PATH = '/contact';

export default function ChatWidget() {
  const { t } = useLanguage();
  const c = t.chat;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null); // init lazily after language loads
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef(null);

  // Reset welcome message when language changes
  useEffect(() => {
    setMessages([{ from: 'bot', text: c.welcome }]);
  }, [c.welcome]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, typing]);

  function send() {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages(prev => [...prev, { from: 'user', text }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { from: 'bot', isReply: true }]);
    }, 900);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  // Parse botReply template: replace {link} with a clickable anchor
  function renderReply() {
    const parts = c.botReply.split('{link}');
    return (
      <>
        {parts[0]}
        <a href={CONTACT_PATH} className="cw-link">{c.linkText}</a>
        {parts[1]}
      </>
    );
  }

  if (!messages) return null;

  return (
    <div className="cw-root">
      {open && (
        <div className="cw-window">
          <div className="cw-header">
            <span className="cw-avatar">🤖</span>
            <span className="cw-title">{c.title}</span>
            <button className="cw-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="cw-body">
            {messages.map((msg, i) => (
              <div key={i} className={`cw-row cw-row--${msg.from}`}>
                {msg.from === 'bot' && <span className="cw-bot-icon">🤖</span>}
                <div className={`cw-bubble cw-bubble--${msg.from}`}>
                  {msg.isReply ? renderReply() : msg.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="cw-row cw-row--bot">
                <span className="cw-bot-icon">🤖</span>
                <div className="cw-bubble cw-bubble--bot cw-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="cw-footer">
            <input
              className="cw-input"
              placeholder={c.placeholder}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
            />
            <button className="cw-send" onClick={send}>{c.send}</button>
          </div>
        </div>
      )}

      <button className="cw-fab" onClick={() => setOpen(v => !v)} aria-label={c.title}>
        {open ? '✕' : '💬'}
      </button>
    </div>
  );
}
